import { useState, useRef, useEffect, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle, Table, ArrowRight, X, ClockCounterClockwise, Trash, PencilSimple } from "@phosphor-icons/react";

const ACCEPT = ".csv,.xlsx,.xls,.pdf,.docx";

function DropZone({ label, sublabel, dataset, onUploaded, onClear, testid }) {
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/datasets/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(data);
      toast.success(`${data.filename} loaded · ${data.row_count} rows`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  if (dataset) {
    return (
      <div
        data-testid={`${testid}-loaded`}
        className="border border-primary bg-primary/[0.03] p-6 flex flex-col"
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary text-white flex items-center justify-center">
              <CheckCircle size={22} weight="fill" />
            </div>
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.2em] text-primary">{label}</div>
              <div className="font-semibold text-foreground truncate max-w-[220px]">{dataset.filename}</div>
            </div>
          </div>
          <button data-testid={`${testid}-clear`} onClick={onClear} className="text-muted-foreground hover:text-destructive transition-colors">
            <X size={18} />
          </button>
        </div>
        <div className="mt-4 flex items-center gap-4 font-mono text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5"><Table size={14} /> {dataset.columns.length} columns</span>
          <span>{dataset.row_count} rows</span>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={testid}
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files?.[0]); }}
      className={`cursor-pointer border-2 border-dashed p-8 flex flex-col items-center justify-center text-center transition-colors min-h-[180px] ${
        drag ? "border-primary bg-primary/5" : "border-border hover:border-primary/60 hover:bg-secondary"
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        data-testid={`${testid}-input`}
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      <div className="font-mono text-xs uppercase tracking-[0.2em] text-primary mb-1">{label}</div>
      <div className="font-semibold">{busy ? "Parsing…" : "Drop file or click to browse"}</div>
      <div className="text-sm text-muted-foreground mt-1">{sublabel}</div>
    </div>
  );
}

function SourceLibrary({ sources, loading, selectedIds, onPick, onDelete, onRename }) {
  if (loading) {
    return <div className="font-mono text-xs text-muted-foreground animate-pulse">Loading recent sources…</div>;
  }
  if (!sources.length) return null;

  const shown = sources.slice(0, 12);
  return (
    <div data-testid="source-library">
      <div className="flex items-center gap-2 mb-3">
        <ClockCounterClockwise size={16} className="text-muted-foreground" />
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
          Recent sources · reuse without re-uploading
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {shown.map((s) => {
          const active = selectedIds.includes(s.id);
          return (
            <div
              key={s.id}
              data-testid={`source-chip-${s.id}`}
              className={`group flex items-center gap-2 border pl-3 pr-1.5 py-1.5 transition-colors ${
                active
                  ? "border-primary bg-primary/[0.06] text-primary"
                  : "border-border bg-white hover:border-primary/60"
              }`}
            >
              <button
                data-testid={`source-chip-pick-${s.id}`}
                onClick={() => !active && onPick(s)}
                disabled={active}
                className="flex items-center gap-2 text-left"
                title={active ? "Already selected" : "Use this source"}
              >
                {active && <CheckCircle size={14} weight="fill" className="shrink-0" />}
                <span className="text-sm font-medium truncate max-w-[180px]">{s.filename}</span>
                <span className="font-mono text-[10px] text-muted-foreground">
                  {s.row_count}r · {s.column_count}c
                </span>
                {s.is_own === false && s.owner_name && (
                  <span className="font-mono text-[10px] text-primary/70 border-l border-border pl-1.5">
                    {s.owner_name}
                  </span>
                )}
              </button>
              <button
                data-testid={`source-chip-rename-${s.id}`}
                onClick={() => onRename(s)}
                className="text-muted-foreground hover:text-primary transition-colors p-1 opacity-0 group-hover:opacity-100"
                title="Rename source"
              >
                <PencilSimple size={13} />
              </button>
              <button
                data-testid={`source-chip-delete-${s.id}`}
                onClick={() => onDelete(s)}
                className="text-muted-foreground hover:text-destructive transition-colors p-1 opacity-0 group-hover:opacity-100"
                title="Remove from library"
              >
                <Trash size={13} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function UploadStep({ datasetA, datasetB, setDatasetA, setDatasetB, onNext }) {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/datasets");
      setSources(data);
    } catch (err) {
      console.warn("Could not load source library (non-critical).", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const handleUploaded = (setter) => (data) => {
    setter(data);
    reload();
  };

  const pickSource = async (s) => {
    if (datasetA && datasetB) {
      toast.info("Both slots are full. Clear one to swap in this source.");
      return;
    }
    try {
      const { data } = await api.get(`/datasets/${s.id}`);
      if (!datasetA) setDatasetA(data);
      else setDatasetB(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load source");
    }
  };

  const deleteSource = async (s) => {
    try {
      await api.delete(`/datasets/${s.id}`);
      setSources((prev) => prev.filter((x) => x.id !== s.id));
      if (datasetA?.id === s.id) setDatasetA(null);
      if (datasetB?.id === s.id) setDatasetB(null);
      toast.success(`${s.filename} removed from library`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  const openRename = (s) => {
    setRenameTarget(s);
    setRenameValue(s.filename);
  };

  const saveRename = async () => {
    const filename = renameValue.trim();
    if (!filename) {
      toast.error("Name cannot be empty");
      return;
    }
    setRenameSaving(true);
    try {
      await api.patch(`/datasets/${renameTarget.id}`, { filename });
      setSources((prev) => prev.map((x) => (x.id === renameTarget.id ? { ...x, filename } : x)));
      if (datasetA?.id === renameTarget.id) setDatasetA({ ...datasetA, filename });
      if (datasetB?.id === renameTarget.id) setDatasetB({ ...datasetB, filename });
      toast.success("Source renamed");
      setRenameTarget(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Rename failed");
    } finally {
      setRenameSaving(false);
    }
  };

  const selectedIds = [datasetA?.id, datasetB?.id].filter(Boolean);

  return (
    <div>
      <div className="mb-8">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-2">Step 1 / 3</div>
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Ingest two datasets</h2>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          Upload datasets for comparison, or reuse a recent source below. Supports CSV, Excel, PDF and Word tables.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <DropZone
          testid="dropzone-a"
          label="Dataset A"
          sublabel="CSV, XLSX, XLS, PDF, DOCX"
          dataset={datasetA}
          onUploaded={handleUploaded(setDatasetA)}
          onClear={() => setDatasetA(null)}
        />
        <DropZone
          testid="dropzone-b"
          label="Dataset B"
          sublabel="CSV, XLSX, XLS, PDF, DOCX"
          dataset={datasetB}
          onUploaded={handleUploaded(setDatasetB)}
          onClear={() => setDatasetB(null)}
        />
      </div>

      {(loading || sources.length > 0) && (
        <div className="mt-8 border-t border-border pt-6">
          <SourceLibrary
            sources={sources}
            loading={loading}
            selectedIds={selectedIds}
            onPick={pickSource}
            onDelete={deleteSource}
            onRename={openRename}
          />
        </div>
      )}

      <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent data-testid="source-rename-dialog">
          <DialogHeader>
            <DialogTitle>Rename source</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label htmlFor="source-rename-input">Display name</Label>
            <Input
              id="source-rename-input"
              data-testid="source-rename-input"
              autoFocus
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") saveRename(); }}
            />
            <p className="text-xs text-muted-foreground">
              This only changes how the source is labeled here — the data is unchanged.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRenameTarget(null)}>Cancel</Button>
            <Button data-testid="source-rename-save" onClick={saveRename} disabled={renameSaving}>
              {renameSaving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="mt-8 flex justify-end">
        <Button
          data-testid="upload-continue-button"
          disabled={!datasetA || !datasetB}
          onClick={onNext}
          className="gap-2 h-11 px-6 group"
        >
          Map columns
          <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
        </Button>
      </div>
    </div>
  );
}
